import { useState } from "react";
import { useLenis } from "lenis/react";
import { Reveal } from "./Reveal";

const fmtM = (n) => (n >= 1000 ? `£${(n / 1000).toFixed(2)}B` : `£${n.toFixed(1)}M`);

export default function Simulator() {
  const lenis = useLenis();
  const [revenue, setRevenue] = useState("250");
  const [margin, setMargin] = useState("5");

  const rev = Math.max(0, Number(revenue) || 0);
  const m = Math.max(0, Number(margin) || 0);
  const profit = rev * (m / 100);
  const low = profit * 0.08;
  const high = profit * 0.12;
  const decisions = Math.round((rev * 200) / 30);

  const field =
    "w-full border border-white/10 bg-[#050505] px-4 py-3 font-mono text-sm text-[#00FF41] outline-none transition-colors duration-300 focus:border-[#00FF41]";

  return (
    <section className="border-b border-white/10 bg-[#0A0A0B]" data-testid="simulator-section">
      <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
        <Reveal>
          <h2 className="max-w-5xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="simulator-heading">
            See your own <span className="text-[#00FF41]">number.</span>
          </h2>
          <p className="mt-8 max-w-2xl font-mono text-sm leading-relaxed text-zinc-400">
            A 1% improvement in realised price moves operating profit by 8–12% for a typical cost
            structure. No other lever compares. Run yours.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <div className="scanlines border border-white/10 bg-[#0F0F11]">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">axiosera — lift simulator</span>
              <span className="font-mono text-[11px] text-[#00FF41] blink" />
            </div>
            <div className="grid gap-8 p-8 lg:grid-cols-12 lg:p-12">
              <div className="space-y-6 lg:col-span-4">
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Annual revenue · £M</label>
                  <input
                    type="number"
                    min="0"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    data-testid="sim-revenue-input"
                    className={`${field} mt-3`}
                  />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Operating margin · %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={margin}
                    onChange={(e) => setMargin(e.target.value)}
                    data-testid="sim-margin-input"
                    className={`${field} mt-3`}
                  />
                </div>
                <button
                  data-testid="sim-cta-button"
                  onClick={() => lenis?.scrollTo("#pilot", { offset: -72, duration: 1.6 })}
                  className="w-full bg-[#00FF41] px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
                >
                  Prove it in a 90-day pilot →
                </button>
              </div>

              <div className="lg:col-span-8">
                <pre className="font-mono text-xs leading-loose sm:text-sm" data-testid="sim-output">
                  <div className="text-zinc-500">$ axiosera simulate --revenue £{rev}M --margin {m}%</div>
                  <div className="mt-4 flex justify-between border-b border-white/5 pb-3">
                    <span className="text-zinc-500">operating profit (est.)</span>
                    <span className="text-white">{fmtM(profit)}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 py-3">
                    <span className="text-zinc-500">+1% realised price →</span>
                    <span className="text-[#00FF41]" data-testid="sim-uplift-output">+{fmtM(low)} – {fmtM(high)} / year</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 py-3">
                    <span className="text-zinc-500">governed pricing decisions (est.)</span>
                    <span className="text-white">~{decisions.toLocaleString()} / day</span>
                  </div>
                  <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-zinc-600">
                    basis: 1% price → 8–12% operating profit · typical cost structure
                  </div>
                </pre>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
